import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, message, Popconfirm, Alert } from 'antd';
import {
    PlusOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const CronManager = ({ vhostId, vhostName }) => {
    const { t } = useTranslation();
    const [crons, setCrons] = useState([]);
    const [cronStats, setCronStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        loadCrons();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vhostId]);

    const loadCrons = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getCrons(vhostId);
            if (response.success) {
                const data = response.data || {};
                setCrons(data.items || data || []);
                setCronStats({
                    max: data.max_cron ?? null,
                    count: data.count ?? (Array.isArray(data.items) ? data.items.length : 0)
                });
            } else {
                message.error(response.message || t('Failed to load scheduled task list'));
            }
        } catch (error) {
            message.error(t('Failed to load scheduled task list'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        form.resetFields();
        setModalVisible(true);
    };

    const handleSubmit = async (values) => {
        try {
            const schedule = (values.schedule || '').trim();
            const command = (values.command || '').trim();

            if (!isValidCronExpression(schedule)) {
                message.error(t('Invalid cron expression format'));
                return;
            }

            const response = await hostSharedService.createCron(vhostId, {
                schedule,
                command
            });

            if (response.success) {
                message.success(t('Scheduled task created'));
                setModalVisible(false);
                form.resetFields();
                loadCrons();
            } else {
                message.error(response.message || t('Creation failed'));
            }
        } catch (error) {
            message.error(t('Creation failed'));
        }
    };

    const handleDelete = async (cronId) => {
        try {
            const response = await hostSharedService.deleteCron(vhostId, cronId);
            if (response.success) {
                message.success(t('Scheduled task deleted'));
                loadCrons();
            } else {
                message.error(response.message || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    const columns = [
        {
            title: t('Task name'),
            dataIndex: 'name',
            key: 'name',
            render: (name) => name || '-'
        },
        {
            title: t('Cron expression'),
            key: 'schedule',
            render: (_, record) => {
                if (record.expression) {
                    return record.expression;
                }
                return `${record.minute || '*'} ${record.hour || '*'} ${record.day || '*'} ${record.month || '*'} ${record.weekday || '*'}`;
            }
        },
        {
            title: t('Request URL'),
            dataIndex: 'command',
            key: 'command',
            ellipsis: true
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-'
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Popconfirm
                    title={t('Delete this scheduled task?')}
                    onConfirm={() => handleDelete(record.id)}
                >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                        {t('Delete')}
                    </Button>
                </Popconfirm>
            )
        }
    ];

    return (
        <Card
            title={t('Scheduled tasks')}
            extra={
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    {t('Create task')}
                </Button>
            }
        >
            <Alert
                message={t('About scheduled tasks')}
                description={
                    <div>
                        <p>{t('Schedule tasks using Cron syntax. Format: minute hour day month weekday (e.g. 0 2 * * * runs daily at 2 AM)')}</p>
                        {cronStats && (
                            <p style={{ marginTop: 8 }}>
                                {t("Currently used: {{value}}{{value2}}", { value: cronStats.count ?? 0, value2: cronStats.max ? t(' / {{max}} tasks', { max: cronStats.max }) : t(' tasks') })}
                            </p>
                        )}
                    </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Table
                dataSource={crons}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{
                    pageSize: 10,
                    showTotal: (total) => t('{{total}} tasks total', { total: total })
                }}
            />

            <Modal
                title={t('Create scheduled task')}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                width={600}
            >
                <Form form={form} onFinish={handleSubmit} layout="vertical">
                    <Alert
                        message={t('Cron expression format')}
                        description={
                            <div>
                                <p>{t('Format: minute hour day month weekday')}</p>
                                <p>{t('Example:')}</p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>{t('0 2 * * * - Run daily at 2 AM')}</li>
                                    <li>{t('*/5 * * * * - Run every 5 minutes')}</li>
                                    <li>{t('0 0 * * 0 - Run at midnight every Sunday')}</li>
                                    <li>{t('0 0 1 * * - Run at midnight on the 1st of each month')}</li>
                                </ul>
                            </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Form.Item
                        name="schedule"
                        label={t('Cron expression')}
                        rules={[{ required: true, message: t('Please enter a Cron expression') }]}
                        extra={t('Format: minute hour day month weekday (e.g., 0 2 * * * runs daily at 2 AM)')}
                    >
                        <Input placeholder={t('E.g. 0 2 * * *')} />
                    </Form.Item>

                    <Form.Item
                        name="command"
                        label={t('Request URL')}
                        rules={[
                            { required: true, message: t('Enter the request URL') },
                            {
                                pattern: /^https?:\/\//i,
                                message: t('Please enter a URL starting with http:// or https://')
                            }
                        ]}
                    >
                        <Input placeholder={t('E.g. https://example.com/cron.php')} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

const isValidCronExpression = (expr) => {
    if (!expr) return false;
    const parts = expr.trim().split(/\s+/);
    return parts.length === 5;
};

export default CronManager;
